import { LegacyRef, useCallback, useEffect } from 'react';

import { Menu, MenuButton, MenuItem, MenuList, rem } from '@vimeo/bokeh';
import { ChevronRightSmall } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Callable } from '@vimeo/core/types/generic';

import { useControlledPopover } from '../../../hooks/dom/useControlledPopover';
import { useInteractionToolsPageLink } from '../../../hooks/interaction_tools/useInteractionToolsPageLink';
import {
  TNotificationHandler,
  useSimpleNotification,
} from '../../../hooks/live/useActionConfirmation';
import {
  T_COPY_LINK,
  T_ENGAGEMENT_TOOLS,
  T_LINK_COPIED,
  T_OPEN_IN_NEW_TAB,
} from '../../../translations';
import { openNewFocusedTab } from '../../../utils/general/redirect';

interface IInteractionToolsSubmenuProps extends IBaseDomElementProps {
  placement: string;
  onCloseParentMenu: Callable;
}

export function InteractionToolsSubmenu({
  placement,
  onCloseParentMenu,
}: IInteractionToolsSubmenuProps) {
  const { triggerRef, contentRef, isOpen, onOpenPopover, onClosePopover } = useControlledPopover();
  const { isLoading, value, hasCopied, onCopy } = useInteractionToolsPageLink();
  const notify: TNotificationHandler = useSimpleNotification();

  const onCopyLinkClick = useCallback(async () => {
    // todo: tracking?
    onCopy();
  }, [onCopy]);

  const onOpenLinkClick = useCallback(async () => {
    openNewFocusedTab(value);
    onClosePopover();
    onCloseParentMenu();
  }, [onCloseParentMenu, onClosePopover, value]);

  useEffect(() => {
    if (hasCopied) {
      onClosePopover();
      onCloseParentMenu();
      notify({ message: T_LINK_COPIED });
    }
  }, [hasCopied, notify, onCloseParentMenu, onClosePopover]);

  return (
    <Menu
      isOpen={isOpen}
      placement={placement}
      closeOnSelect={false}
      onOpen={onOpenPopover}
      onClose={onClosePopover}
    >
      <MenuButton
        as={MenuItem}
        ref={triggerRef}
        command={(<ChevronRightSmall boxSize={rem(20)} />) as unknown as string}
        commandSpacing={0}
      >
        {T_ENGAGEMENT_TOOLS}
      </MenuButton>
      <MenuList ref={contentRef as LegacyRef<HTMLDivElement>} width={rem(168)}>
        <MenuItem isDisabled={isLoading} onClick={onCopyLinkClick}>
          {T_COPY_LINK}
        </MenuItem>
        <MenuItem isDisabled={isLoading} onClick={onOpenLinkClick}>
          {T_OPEN_IN_NEW_TAB}
        </MenuItem>
      </MenuList>
    </Menu>
  );
}
